import React from 'react';

import { format } from 'date-fns';

import {
    BASIS_HEIGHT,
    BASIS_LABEL_WIDTH,
    BASIS_LINE_COUNT,
    BASIS_LINE_HEIGHT,
    BASIS_LINES_TOP,
    BASIS_NOTE_GAP,
    BASIS_NOTE_SIZE,
    box,
    CONTENT_WIDTH,
    lineHeight,
    MARGIN_X,
    pt,
    statusLinesHeight,
} from '../slideGeometry';

import { StatusLines } from './StatusRulesBlock';

const INSET_X = 16;

/**
 * The basis of preparation, then the status rules the pack applied under a
 * heading and introduction of their own, as in the .pptx: each value at the size the
 * Paginator fitted it to (`fit.values`), its line growing for every line the
 * value wraps to, and each rule in the slot the Paginator measured for it.
 */
const BasisBlock = ({ block }) => {
    const lines = [
        [$t('Figures as at'), block.asAtLabel || block.asAt],
        [$t('Compared with'), block.comparisonBudget],
        [$t('Movement since'), block.movementBasis],
        [$t('Currency'), block.currency],
        [$t('Budget figures'), block.budgetFigures],
        [$t('Generated'), block.generatedAtLabel || (block.generatedAt ? format(new Date(block.generatedAt), 'yyyy-MM-dd HH:mm') : $t('Not yet generated'))],
        ...(block.contingency ? [[$t('Contingency'), block.contingency]] : []),
    ];

    let y = BASIS_LINES_TOP;
    const rows = lines.map(([label, value], index) => {
        const fit = block.fit?.values?.[index] ?? { size: 10, lines: 1 };
        const height = BASIS_LINE_HEIGHT + (fit.lines - 1) * lineHeight(fit.size);
        const row = { label, value, size: fit.size, y, height };
        y += height;

        return row;
    });

    const noteTop = y + BASIS_NOTE_GAP;
    const noteHeight = block.asAtNote ? (block.fit?.noteLines ?? 1) * lineHeight(BASIS_NOTE_SIZE) : 0;
    const statusHeight = (block.statusLines || []).length ? statusLinesHeight(block) : 0;
    const height =
        block.height ??
        BASIS_HEIGHT -
            (BASIS_LINE_COUNT - lines.length) * BASIS_LINE_HEIGHT +
            (y - BASIS_LINES_TOP - lines.length * BASIS_LINE_HEIGHT) +
            (noteHeight ? BASIS_NOTE_GAP + noteHeight : 0) +
            statusHeight;

    return (
        <div className="tw-bg-blue-10" style={box(MARGIN_X, block.y, CONTENT_WIDTH, height)}>
            <div className="tw-text-dark-blue tw-font-bold" style={{ ...box(INSET_X, 10, 300, 18), fontSize: pt(11) }}>
                {$t('Basis of preparation')}
            </div>
            {rows.map((row, index) => (
                <div key={index} style={box(INSET_X, row.y, CONTENT_WIDTH - 2 * INSET_X, row.height)} className="tw-flex" dir="auto">
                    <span className="tw-text-charcoal-60 tw-shrink-0" style={{ width: BASIS_LABEL_WIDTH, fontSize: pt(10) }}>
                        {row.label}
                    </span>
                    <span className="tw-text-charcoal" style={{ fontSize: pt(row.size) }}>
                        {row.value}
                    </span>
                </div>
            ))}
            {block.asAtNote && (
                <div
                    className="tw-text-charcoal-60 tw-italic"
                    dir="auto"
                    style={{
                        ...box(INSET_X, noteTop, CONTENT_WIDTH - 2 * INSET_X, noteHeight),
                        fontSize: pt(BASIS_NOTE_SIZE),
                        lineHeight: `${lineHeight(BASIS_NOTE_SIZE)}px`,
                    }}
                >
                    {block.asAtNote}
                </div>
            )}
            <StatusLines block={block} top={height - statusHeight} />
        </div>
    );
};

export default BasisBlock;
